/**
 * Budget Constants - FinList Budget System
 * 
 * Extracted from budgetService.js for better modularity.
 * These constants define the core budget configuration types.
 */

/**
 * Budget cadence types
 * Defines how often a budget period repeats
 */
export const BUDGET_CADENCE = {
  MONTHLY: 'MONTHLY',
  WEEKLY: 'WEEKLY',
  CUSTOM: 'CUSTOM'
};

/**
 * Budget templates - Strategy-based
 * Pre-defined budget allocation strategies
 */
export const BUDGET_TEMPLATES = {
  ZERO_BASED: 'ZERO_BASED',
  FIFTY_THIRTY_TWENTY: '50_30_20',
  ESSENTIALS_FIRST: 'ESSENTIALS_FIRST',
  CUSTOM: 'CUSTOM'
};

/**
 * Budget templates - Demographic/Lifestyle-based
 * Pre-built templates for common life situations
 */
export const DEMOGRAPHIC_TEMPLATES = {
  STUDENT: 'STUDENT',
  SINGLE_PROFESSIONAL: 'SINGLE_PROFESSIONAL',
  COUPLE_NO_KIDS: 'COUPLE_NO_KIDS',
  FAMILY_OF_4: 'FAMILY_OF_4',
  RETIRED: 'RETIRED'
};

/**
 * Demographic template configurations
 * Defines default category allocations as percentages
 */
export const DEMOGRAPHIC_TEMPLATE_CONFIG = {
  STUDENT: {
    name: 'Student Budget',
    icon: '🎓',
    tagline: 'Stretch your student income',
    description: 'Designed for students with limited income, focusing on essentials and education.',
    allocations: {
      'housing-rent': 0.35,
      'groceries-food': 0.15,
      'transport-public': 0.10,
      'education': 0.10,
      'entertainment': 0.10,
      'personal-care': 0.05,
      'emergency-fund': 0.10,
      'other': 0.05
    }
  },
  SINGLE_PROFESSIONAL: {
    name: 'Single Professional',
    icon: '💼',
    tagline: 'Balance work and lifestyle',
    description: 'For working professionals living alone, with room for savings and lifestyle.',
    allocations: {
      'housing-rent': 0.30,
      'groceries-food': 0.12,
      'transport-fuel': 0.10,
      'utilities': 0.05,
      'dining-out': 0.08,
      'entertainment': 0.07,
      'personal-care': 0.03,
      'savings-general': 0.15,
      'emergency-fund': 0.05,
      'investments': 0.05
    }
  },
  COUPLE_NO_KIDS: {
    name: 'Couple (No Kids)',
    icon: '💑',
    tagline: 'Build your future together',
    description: 'For couples without children, balancing shared expenses and savings goals.',
    allocations: {
      'housing-rent': 0.28,
      'groceries-food': 0.12,
      'utilities': 0.05,
      'transport-fuel': 0.08,
      'dining-out': 0.06,
      'entertainment': 0.06,
      'travel-vacation': 0.05,
      'personal-care': 0.04,
      'savings-general': 0.12,
      'investments': 0.08,
      'emergency-fund': 0.06
    }
  },
  FAMILY_OF_4: {
    name: 'Family of 4',
    icon: '👨‍👩‍👧‍👦',
    tagline: 'Manage household finances',
    description: 'For families with 2 children, covering childcare, education, and family needs.',
    allocations: {
      'housing-rent': 0.30,
      'groceries-food': 0.15,
      'utilities': 0.06,
      'transport-fuel': 0.08,
      'childcare': 0.10,
      'education': 0.05,
      'healthcare': 0.04,
      'entertainment': 0.04,
      'personal-care': 0.03,
      'savings-general': 0.08,
      'emergency-fund': 0.05,
      'other': 0.02
    }
  },
  RETIRED: {
    name: 'Retired',
    icon: '🏖️',
    tagline: 'Enjoy your golden years',
    description: 'For retirees living on fixed income, focusing on healthcare and leisure.',
    allocations: {
      'housing-rent': 0.30,
      'groceries-food': 0.15,
      'utilities': 0.08,
      'healthcare': 0.12,
      'transport-public': 0.05,
      'entertainment': 0.08,
      'travel-vacation': 0.07,
      'gifts-donations': 0.05,
      'emergency-fund': 0.05,
      'other': 0.05
    }
  }
};

/**
 * Budget status
 * Lifecycle states for a budget period
 */
export const BUDGET_STATUS = {
  OPEN: 'OPEN',
  LOCKED: 'LOCKED',
  CLOSED: 'CLOSED'
};

/**
 * Rollover rules
 * How surplus/deficit carries between budget periods
 */
export const ROLLOVER_RULES = {
  CARRY: 'CARRY',
  SAVE: 'SAVE',
  RESET: 'RESET'
};
